import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EnhancedInputField } from './EnhancedInputField';
import axios from 'axios';
import { API } from '../../shared/api/Api';
import { showToast } from '../../utils/utils';
import { useSelector } from 'react-redux';
import { selectUserInformations } from '../../features/authSlice';

const UserItems = ({
    field,
    formData,
    type,
    handleChange,
    disabled = false,
    accountType = 'instructor',
}) => {
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const user = useSelector(selectUserInformations);

    // Create a function to compute params based on the type and accountType
    const getParams = useCallback((page = 1) => {
        const baseParams = {
            page,
            limit: 20,
            search: "",
        };

        if (type === 'add') {
            return {
                ...baseParams,
                onlyNewUsers: 1,
            };
        } else if (type === 'edit' || type === 'preview') {
            return {
                ...baseParams,
                onlyInstructors: accountType === 'instructor' && accountType !== 'materials' ? 1 : 0,
                onlyStudents: accountType === 'student' && accountType !== 'materials' ? 1 : 0,
            };
        }
        return baseParams;
    }, [type, accountType]);

    // Fetch users based on computed parameters
    const fetchUsers = useCallback(async (page = 1) => {
        try {
            const response = await axios.get(API.Users.app_users_allUsers, {
                params: getParams(page),
                headers: { Authorization: `Bearer ${user.token}` },
            });
            const data = response.data;

            if (data.status === 200 && data.errorCode === 0) {
                if (page === 1) {
                    setUsers(data.data.items);
                } else {
                    setUsers((prev) => [...prev, ...data.data.items]);
                }
                setCurrentPage(page);
                setHasMore(data.data.current_page < data.data.last_page);
                return true;
            } else {
                showToast("Error", "Failed to fetch users", "error");
                return false;
            }
        } catch (error) {
            showToast(
                "Error",
                error.response?.data?.message || "An error occurred",
                "error"
            );
            return false;
        }
    }, [user.token, getParams]);

    // Load more users with pagination
    const loadMoreUsers = useCallback(async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        await fetchUsers(currentPage + 1);
        setLoading(false);
    }, [currentPage, hasMore, loading, fetchUsers]);

    // Initial load
    useEffect(() => {
        const fetchData = async () => {
            setInitialLoading(true);
            await fetchUsers(1);
            setInitialLoading(false);
        };
        fetchData();
    }, [fetchUsers]);

    const options = useMemo(
        () =>
            users.map((item) => ({
                uuid: item.uuid,
                id: item.uuid,
                name: item.name,
                title: item.name,
            })),
        [users]
    );

    if (initialLoading) {
        return (
            <div className="flex items-center justify-center py-4">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <EnhancedInputField
            key={field.name}
            label={field.label}
            name={field.name}
            type={field.type}
            placeholder={field.placeholder}
            value={formData[field.name]}
            handleChange={handleChange}
            options={options}
            disabled={disabled}
            onLoadMore={hasMore ? loadMoreUsers : null}
            hasMore={hasMore}
            loading={loading}
        />
    );
};

export default UserItems;
